import { HttpClient, HttpHeaders, HttpResponse } from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { ToastrService } from "ngx-toastr";
import { JwtHelperService } from "@auth0/angular-jwt";
import { AuthService } from "./auth.service";
import { Injectable } from "@angular/core";

@Injectable()
export class BaseService {
    http: HttpClient;
    headers: HttpHeaders = new HttpHeaders();
    toastr: ToastrService;
    authService: AuthService;
    jwtHelper: JwtHelperService;

    constructor(httpDec: HttpClient, authService: AuthService, toastr: ToastrService) {
        this.http = httpDec;
        this.toastr = toastr;
        this.authService = authService;
        this.jwtHelper = new JwtHelperService();
        this.headers = new HttpHeaders();
        this.headers = this.headers.set("Content-Type", "application/json;charset=utf-8");
    }

    get(url: string): Observable<any> {
        const headers = this.getHeaders(url);
        return this.intercept(this.http.get(url, { headers }));
    }

    getText(url: string): Observable<string> {
        const headers = this.getHeaders(url);
        return this.intercept(this.http.get(url, { headers, responseType: 'text' }));
    }

    getWithContentType(url: string, contentType: string): Observable<Blob> {
        const headers = this.getHeaders(url).set("Content-Type", contentType);
        return this.intercept(this.http.get(url, { headers, responseType: 'blob' }));
    }

    postWithContentType(url: string, contentType: string, data: any): Observable<Blob> {
        const headers = this.getHeaders(url).set("Content-Type", contentType);
        return this.intercept(this.http.post(url, data, { headers, responseType: 'blob' }));
    }

    /**
     * Makes a http put request to the url provided, setting the
     * headers and stringifying the data
     * @param url The url to put too
     * @param data The data to stringify and put
     */
    put(url: string, data: any): Observable<any> {
        const headers = this.getHeaders(url);
        return this.intercept(this.http.put(url, data, { headers }));
    }

    /**
     * Makes a http post request to the url provided, setting the
     * headers and stringifying the data
     * @param url The url to post too
     * @param data The data to stringify and post
     */
    post(url: string, data: any): Observable<any> {
        const headers = this.getHeaders(url);
        return this.intercept(this.http.post(url, data, { headers }));
    }

    delete(url: string, data: any): Observable<any> {
        const headers = this.getHeaders(url);
        return this.intercept(this.http.request('DELETE', url, { body: data, headers }));
    }

    /**
     * Sets the headers for the request
     * @param url url to request
     */
    private getHeaders(url: string): HttpHeaders {
        let headers = this.headers;

        // Log business function details
        const businessFunctionId = AuthService.businessFunctionId;
        const businessFunction = AuthService.businessFunction;
        const accessToken = AuthService.accessToken;

        if (businessFunctionId != null) {
            headers = headers.set("BusinessFunctionId", businessFunctionId.toString());
        }

        // set auth token if available
        if (accessToken != null) {
            // Log token expiration details
            const tokenExpirationDate: Date | null = this.jwtHelper.getTokenExpirationDate(accessToken);
            const currentDate = new Date();

            headers = headers.set("Authorization", `Bearer ${accessToken}`);

            // this could occur if the user has access to multiple business functions
            // if the previous user was signed in and then select a business function
            // that does require authentication, we want to remove the Authorization header
            if (businessFunction != null) {
                if (!businessFunction.authenticationRequired && url != "/api/ApplicationUser") {
                    headers = headers.delete("Authorization");
                }

                if (businessFunction.authenticationRequired) {
                    let tokenExpirationDate: Date | null = this.jwtHelper.getTokenExpirationDate(accessToken);
                    if (tokenExpirationDate && tokenExpirationDate < new Date()) {
                        this.authService.accessTokenExpired = true;
                    }
                }
            } 
        } 
        return headers;
    }

    /**
     * @internal checks for error messages that may occur from requests.
     * If a 401 error occurs, the user will be redirected, for other errors
     * toasts will be shown to the user
     * @param observable The response from the request made
     */
    private intercept<T>(observable: Observable<T>): Observable<T> {
        // put logging in to capture error on client side when timed out
        return observable.pipe(
            catchError((err) => {
                if (err.error && err.error instanceof Blob) {
                    this.toastr.error("Sorry an error occurred downloading the requested file.");
                } else if (err.status === 401 || err.status === 403) {
                    this.toastr.error(err.error ? err.error : "You do not have authorization to access this resource");
                    this.authService.logOut = true;
                } else if (err.status === 0 || err.status === 440) {
                    this.toastr.error("Session timed out.");
                    this.authService.logOut = true;
                } else {
                    if (err.status === 404 || err.status === 400 || err.status === 500) {
                        this.toastr.error(err.error);
                    } else {
                        this.toastr.error("Sorry an error occurred. Please contact your system administrator.");
                    }
                }
                return throwError(err);
            })
        );
    }
}